/**
 * Safe wiring of Elm ports.
 *
 * Elm dead-code-eliminates a port that no Elm module uses any more, so
 * `app.ports.<name>` can be `undefined` even though the port is still declared
 * in `frontend/src`. Touching `.subscribe`/`.send` on that `undefined` throws,
 * and since the port wiring is one long run of top-level statements, a single
 * orphaned port takes down every port wired after it — silently, from the
 * user's point of view.
 *
 * That is not hypothetical: a `copyText` port left behind by a deleted modal
 * threw at startup and killed the confirmation dialog ("Slett bruker" did
 * nothing), focus handling and scroll reporting along with it.
 *
 * These helpers turn that class of bug into a console warning about one port.
 */

const warnedPorts = new Set();

/**
 * Look up a port, warning once if the Elm side has dropped it.
 *
 * Warning once per port matters: `send` is called from a 1s interval and from
 * the scroll handler, and warning on every call would bury the first message.
 *
 * @param {object} app Elm app
 * @param {string} name Port name
 * @param {'subscribe' | 'send'} method Direction we intend to use it in
 * @returns {object | undefined} The port, or undefined if unusable
 */
function getPort(app, name, method) {
  const port = app?.ports?.[name];
  if (port && typeof port[method] === 'function') {
    return port;
  }
  if (!warnedPorts.has(name)) {
    warnedPorts.add(name);
    console.warn(
      `Elm port "${name}" is wired up in JS but missing from the compiled Elm app, so ${method} does nothing. ` +
        `Either the Elm side no longer uses the port (delete the JS wiring and the port declaration), ` +
        `or its module lost its last import and Elm dropped it.`
    );
  }
  return undefined;
}

/**
 * Subscribe to an outgoing Elm port (Cmd), if it exists.
 *
 * @param {object} app Elm app
 * @param {string} name Port name
 * @param {Function} handler Called with what Elm sends out
 */
export function subscribe(app, name, handler) {
  getPort(app, name, 'subscribe')?.subscribe(handler);
}

/**
 * Send to an incoming Elm port (Sub), if it exists.
 *
 * @param {object} app Elm app
 * @param {string} name Port name
 * @param {*} payload Value to hand to Elm
 */
export function send(app, name, payload) {
  getPort(app, name, 'send')?.send(payload);
}
